import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useStore } from 'effector-react';
import styled from 'styled-components';
import {
  EnterCoverEnter,
  EnterCoverPosition,
  EnterCoverType,
  ImageType,
} from '@types';
import Tabbar from '@components/Tabbar';
import Image from '@components/Image';
import useLangString from '@hooks/useLangString';
import { getMediaUrl } from '@utils/urlMakers';
import { $editingEnterCover } from '@stores/editingStores';
import getImageSize from '@utils/getImageSize';
import limitNum from '@utils/limitNum';
import CoverVideo from './CoverVideo';
import ImgSound from './img-sound.png';

const Background = styled.div<{ bgc: string; img?: string }>`
  background-color: ${(props) => props.bgc};
  background-image: ${(props) => (props.img ? `url(${props.img})` : 'none')};
`;

function EnterCoverLayer() {
  const [tab, setTab] = useState<number>(0);
  const data = useStore($editingEnterCover.store);
  const isImage = data.type === EnterCoverType.image;
  const isVideo = data.type === EnterCoverType.video;
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const [videoTime, setVideoTime] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const pos = tab === 1 ? data.mobileShowPosition : data.pcShowPosition;
  const isCenter = pos === EnterCoverPosition.center;
  const isFullscreen = pos === EnterCoverPosition.fullscreen;
  const coverUrl = tab === 1 ? data.mobileCoverUrl : data.pcCoverUrl;
  const thumb = tab === 1 ? data.mobileCoverVideoThumb : data.pcCoverVideoThumb;

  useEffect(() => {
    setVideoTime(
      Math.ceil(
        (tab === 1
          ? data.mobileCoverVideoDuration
          : data.pcCoverVideoDuration) ?? 0
      )
    );
  }, [coverUrl]);

  useEffect(() => {
    if (thumb) {
      getImageSize(getMediaUrl(thumb)).then((x) => setSize(x));
    } else {
      setSize(null);
    }
  }, [thumb]);

  useEffect(() => {
    if (tab === 1) {
      const resizeHandler = () => {
        if (!containerRef.current) return;
        const height = limitNum({
          min: 550,
          max: 810,
          value: containerRef.current.clientHeight
            ? containerRef.current.clientHeight - 20
            : 0,
        });
        const width = height ? (height / 16) * 9 : 0;
        setStyle({ width, height });
      };
      resizeHandler();
      window.addEventListener('resize', resizeHandler);
      return () => {
        window.removeEventListener('resize', resizeHandler);
        setStyle({});
      };
    }
  }, [tab]);

  const lang = useLangString({ skip: ['s 跳过', 's Skip'] }, []);

  const bgc = `rgba(${data.bgColor})`;
  const bgImg = data.bgImageUrl ? getMediaUrl(data.bgImageUrl) : undefined;

  const renderImage = () => {
    return (
      <>
        <Image
          src={coverUrl!}
          type={ImageType.media}
          className={classNames({
            'max-h-full max-w-full': isCenter,
            'h-full w-full': isFullscreen,
          })}
        />
        {data.enterMethod === EnterCoverEnter.auto && !!data.showCountDown && (
          <div className="absolute top-15 right-15 flex h-30 items-center rounded bg-black-black bg-opacity-50 px-10">
            {`${data.enterDuration}${lang.skip}`}
          </div>
        )}
      </>
    );
  };

  const renderVideo = () => {
    if (!size) return;
    const { width, height } = size;
    return (
      <>
        <CoverVideo
          key={coverUrl}
          src={getMediaUrl(coverUrl)}
          thumb={thumb!}
          className={classNames({
            'max-h-full max-w-full': isCenter,
            'w-full': isFullscreen && width >= height,
            'h-full': isFullscreen && width < height,
          })}
          onTimeChange={(x) => setVideoTime(Math.ceil(x))}
        />
        <div className="absolute top-15 right-15 flex items-center">
          <div className="flex h-30 w-30 items-center justify-center rounded bg-black-black bg-opacity-50">
            <img src={ImgSound} alt="" className="h-16 w-16" />
          </div>
          <div className="ml-10 flex h-30 items-center rounded bg-black-black bg-opacity-50 px-10">
            {`${videoTime}${lang.skip}`}
          </div>
        </div>
      </>
    );
  };

  const renderContent = () => {
    return (
      <Background
        bgc={bgc}
        img={bgImg}
        className="absolute inset-0 flex flex-col items-center justify-center"
      >
        {isImage && renderImage()}
        {isVideo && renderVideo()}
      </Background>
    );
  };

  const renderPc = () => {
    return coverUrl ? (
      renderContent()
    ) : (
      <>
        <div className="text-xl">还未设置桌面端开场封面</div>
        <div className="mt-5 text-black-gray">
          开场封面是进入漫游作品前所显示的内容
        </div>
      </>
    );
  };

  const renderMobile = () => {
    return (
      <div
        style={style}
        className="relative flex flex-col items-center justify-center overflow-hidden rounded bg-black"
      >
        {coverUrl ? (
          renderContent()
        ) : (
          <>
            <div className="text-xl">还未设置移动端开场封面</div>
            <div className="mt-5 text-black-gray">
              开场封面是进入漫游作品前所显示的内容
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center bg-black-#28"
      ref={containerRef}
    >
      {tab === 0 && renderPc()}
      {tab === 1 && renderMobile()}
      <div className="absolute top-15 left-15 w-250">
        <Tabbar
          tabs={['桌面端预览', '移动端预览']}
          activeIndex={tab}
          onChange={setTab}
        />
      </div>
    </div>
  );
}

export default EnterCoverLayer;
